import { LocalStorageKeys } from '@/app'
import { analytics, APIClient } from '@/services'
import { replyStore } from '@/stores'
import { CommunityFeed } from '@/types'
import { useDerivedRef } from '@/utils'
import { globalState } from '@codeleap/store'
import { TypeGuards } from '@codeleap/types'
import { useQuery } from '@tanstack/react-query'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useEffect, useRef } from 'react'
import { createGlobalTimeout } from '../../utils/utility'
import { FeedHelpOverlay } from './atoms/HelpOverlay'
import { FeedDimensions } from './FeedItems/shared'

const seenItems = globalState({ ids: [] })

const viewTimeout = createGlobalTimeout('view-timeout')

const onViewItem = async (item: CommunityFeed) => {
  if (!APIClient.Session.isLoggedIn() || !item?.id) {
    return
  }

  const currentIds = seenItems.get().ids

  if (currentIds.includes(item.id)) {
    return
  }

  const updatedIds = [...currentIds, item.id]
  seenItems.set({ ids: updatedIds })

  viewTimeout.set(() => {
    const finalIds = seenItems.get().ids

    if (finalIds.length > 0) {
      APIClient.Community.markSeen(finalIds)
      seenItems.set({ ids: [] })
    }
  }, 2000)
}

const focusedFeedItem = globalState<number>(null)
const focusedFeedItemId = globalState<CommunityFeed['id']>(null)

const firstItemIndex = 0

export const useIsFocusedFeedItem = (index: number) => focusedFeedItem.use(s => s === index)

export const useFeedScroll = (params: ReturnType<typeof APIClient.Community.useCommunity>['listProps']) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = params

  const isEmpty = data?.length <= 0 || !data?.length

  const parentRef = useRef<HTMLDivElement>(null)

  const itemsRef = useDerivedRef(data)
  const scrolling = useRef(false)
  const scrollRequested = useRef(false)
  const previousScrollTop = useRef(0)
  const goToLocked = useRef(false)
  const analyticsFired = useRef(false)

  const virtualizer = useVirtualizer({
    count: data?.length || 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => FeedDimensions.getHeightNumber(),
    overscan: 3,
    getItemKey: index => {
      const item = data?.[index]
      return `${item?.session_id}-${item?.id ?? index}`
    },
  })

  const onFocusFeedItem = useCallback((index: number) => {
    const changed = focusedFeedItem.get() !== index
    const lastIndex = (itemsRef.current?.length || 1) - 1
    const safeIndex = Math.max(0, Math.min(index, lastIndex))

    if (!changed) return

    focusedFeedItem.set(safeIndex)
    replyStore.resetReplyState()

    const item = itemsRef.current?.[safeIndex]

    focusedFeedItemId.set(item?.id)

    if (item) onViewItem(item)

    if (safeIndex !== firstItemIndex) FeedHelpOverlay.hide()
  }, [])

  const checkAndFetchNextPage = useCallback(() => {
    if (!parentRef.current || isFetchingNextPage || !hasNextPage) return

    const currentIndex = focusedFeedItem.get()
    const lastIndex = itemsRef.current?.length - 1
    const triggerIndex = lastIndex - 5

    const canFetch = currentIndex >= triggerIndex

    if (canFetch && !scrollRequested.current) {
      scrollRequested.current = true

      const authorized = APIClient.UnknownProfile.maybeOpenRegistrationWall(false)

      if (authorized) {
        fetchNextPage()
        setTimeout(() => (scrollRequested.current = false), 6000)
      } else {
        setTimeout(() => (scrollRequested.current = false), 3000)
      }
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const handleScroll = useCallback(() => {
    if (!parentRef.current || scrolling.current) return

    scrolling.current = true

    const scrollTop = parentRef.current.scrollTop
    const itemHeight = FeedDimensions.getHeightNumber()
    const calculatedIndex = Math.round(scrollTop / itemHeight)
    const lastIndex = itemsRef.current?.length - 1
    const currentIndex = Math.max(0, Math.min(lastIndex, calculatedIndex))

    const delta = scrollTop - previousScrollTop.current
    const isScrollingUp = scrollTop < previousScrollTop.current

    if (Math.abs(delta) > 20 && !analyticsFired.current) {
      const direction = delta > 0 ? 'feed_swipe_down' : 'feed_swipe_up'
      analytics.track(direction)
      analyticsFired.current = true
    }

    previousScrollTop.current = scrollTop

    onFocusFeedItem(currentIndex)

    if (isScrollingUp) {
      return (scrolling.current = false)
    }

    if (currentIndex >= lastIndex) {
      const authorized = APIClient.UnknownProfile.maybeOpenRegistrationWall()
      if (!authorized) {
        return setTimeout(() => {
          scrolling.current = false
        }, 3000)
      }
    }

    scrolling.current = false

    checkAndFetchNextPage()
  }, [onFocusFeedItem, checkAndFetchNextPage])

  useEffect(() => {
    if (!isEmpty) onFocusFeedItem(firstItemIndex)
  }, [isEmpty])

  useEffect(() => {
    const handleScrollEnd = () => {
      analyticsFired.current = false
    }

    if (parentRef.current) {
      parentRef.current.addEventListener('scroll', handleScroll)
      parentRef.current.addEventListener('scrollend', handleScrollEnd)
    }

    return () => {
      if (parentRef.current) {
        parentRef.current.removeEventListener('scroll', handleScroll)
        parentRef.current.removeEventListener('scrollend', handleScrollEnd)
      }
    }
  }, [handleScroll])

  const scrollToIndex = useCallback(
    (index: number) => {
      return new Promise<void>((resolve, reject) => {
        scrolling.current = true

        virtualizer.scrollToIndex(index, { behavior: 'smooth' })

        onFocusFeedItem(index)

        setTimeout(() => {
          scrolling.current = false
          resolve()
        }, 800)
      })
    },
    [onFocusFeedItem, virtualizer],
  )

  const goToNext = useCallback(() => {
    if (goToLocked.current || scrolling.current) return

    const current = focusedFeedItem.get()
    const nextIndex = current + 1
    const lastIndex = itemsRef.current?.length - 1

    if (nextIndex > lastIndex) {
      const authorized = APIClient.UnknownProfile.maybeOpenRegistrationWall(true, false, 'community-endReached')
      if (!authorized) return
    }

    checkAndFetchNextPage()

    goToLocked.current = true

    scrollToIndex(nextIndex).then(() => (goToLocked.current = false))
  }, [scrollToIndex, checkAndFetchNextPage])

  const goToPrevious = useCallback(() => {
    if (goToLocked.current || scrolling.current) return

    const current = focusedFeedItem.get()
    const prevIndex = Math.max(0, current - 1)

    goToLocked.current = true

    scrollToIndex(prevIndex).then(() => (goToLocked.current = false))
  }, [scrollToIndex])

  return {
    parentRef,
    goToNext,
    goToPrevious,
    virtualizer,
  }
}

export const useCurrentFeedItem = () => {
  const feedItemId = focusedFeedItemId.use()

  const query = useQuery({
    queryKey: APIClient.Community.communityManager.queryKeys.keys.retrieve(feedItemId),
    queryFn: () => {
      return APIClient.Community.communityManager.queryKeys.getRetrieveData(feedItemId, {
        deepSearch: true,
      })
    },
    enabled: !TypeGuards.isNil(feedItemId),
  })

  return query.data
}
